/**
 * O dado lido de um documento pelo painel (E203.F2, ADR-0146 §4; ADR-0105): o campo da ficha que a
 * linha imprime, com o valor usado, "Ver na fonte" (no painel do original, E201.F2) e o cartão de
 * sempre, "Rever leitura na fonte": a releitura pelo papel ou a correção com o valor, o documento,
 * a página e o motivo. O servidor reconfere o caso e refaz a prévia; a mesa relê o documento.
 */
import { Button } from '@/components/ui';
import { ReverLeitura } from '@/components/FichaDaLeitura/ReverLeitura';
import { ptBR } from '@/i18n/pt-BR';
import type { CampoDaFichaData } from '@/lib/ficha_da_leitura';
import { usePainelDoDocumento } from '@/lib/painelDoDocumento';
import { useFichaDaLeitura } from '@/lib/useFichaDaLeitura';

import type { PropsDoConteudo } from './tipos';

const t = ptBR.painelDoDado;

/**
 * O campo pedido. E205.F3.C01 (F-E205.F3-01): com o referente (a pendência e "Rever leitura"), só o
 * par (campo, referente) serve — com duas CCBs, o primeiro campo com a chave é o da outra; sem o par
 * na ficha, nenhum (o painel diz que o campo não está). Sem referente (o documento impresso), a
 * chave, como sempre.
 */
export function doCampo(
  campos: readonly CampoDaFichaData[],
  chave: string,
  referente?: string | null,
): CampoDaFichaData | null {
  if (referente !== undefined) {
    return campos.find((c) => c.campo === chave && c.referente_id === referente) ?? null;
  }
  return campos.find((c) => c.campo === chave) ?? null;
}

export function CampoLidoNoPainel({ client, caseId, pedido, acoes, onMudou }: PropsDoConteudo) {
  const { ficha, reler } = useFichaDaLeitura(client, caseId, pedido.origem.chave);
  const painel = usePainelDoDocumento();
  if (ficha.estado === 'carregando') {
    return <p className="text-body-sm text-fg-muted">{t.carregando}</p>;
  }
  if (ficha.estado === 'erro') {
    return (
      <div role="alert" className="space-y-sc-2" data-testid="painel-lido-erro">
        <p className="text-body-sm text-danger-700">{t.erro}</p>
        <Button type="button" size="sm" variant="secondary" onClick={reler}>
          {t.tentarDeNovo}
        </Button>
      </div>
    );
  }
  const registro = ficha.estado === 'pronto' ? ficha.dados.ficha : null;
  const campo =
    registro === null
      ? null
      : doCampo(registro.ficha.campos, pedido.origem.chave, pedido.origem.referente);
  if (registro === null || campo === null) {
    return (
      <p className="text-body-sm text-fg-muted" data-testid="painel-lido-sem-campo">
        {t.lido.semCampo}
      </p>
    );
  }
  return (
    <ReverLeitura
      // O cartão pertence ao par (campo, referente): outro par começa do zero (E205.F3.C03).
      key={`${campo.campo}:${campo.referente_id ?? ''}`}
      client={client}
      caseId={caseId}
      campo={campo}
      documentos={registro.ficha.documentos}
      versao={registro.versao}
      onVerNaFonte={(alvo) => painel?.abrir({ documentoId: alvo.documentoId, pagina: alvo.pagina })}
      onFeito={(resultado) => {
        acoes.salvo();
        reler();
        if (resultado === 'mudou') onMudou();
      }}
      onFechar={acoes.fechar}
    />
  );
}
